import { useEffect, useState, useMemo } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import {
  Plus, Trash, FloppyDisk, ArrowClockwise, PencilSimple, CheckCircle, XCircle,
  ArrowUp, ArrowDown, Rocket,
} from "@phosphor-icons/react";

const ITEM_TYPES = [
  { v: "text",           l: "Text (single line)" },
  { v: "textarea",       l: "Long text" },
  { v: "number",         l: "Number" },
  { v: "date",           l: "Date" },
  { v: "phone",          l: "Phone number" },
  { v: "email",          l: "Email" },
  { v: "select",         l: "Dropdown (choose one)" },
  { v: "file",           l: "File upload (single)" },
  { v: "multi_file",     l: "File upload (multiple)" },
  { v: "acknowledgment", l: "Checkbox / I acknowledge" },
  { v: "address",        l: "Structured address" },
];

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

export default function OnboardingConfigTab() {
  const { confirm } = useDialog();
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/onboarding/config");
      setCfg(r.data); setInitial(JSON.parse(JSON.stringify(r.data)));
    } catch (e) { setMsg({ type: "error", text: formatAxiosError(e) }); }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial), [cfg, initial]);

  const flash = (type, text) => {
    setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalize order
      const sections = cfg.sections.map((s, i) => ({ ...s, order: i }));
      const items = cfg.items.map((it, i) => ({ ...it, order: i, key: slug(it.key) || slug(it.label) }));
      const r = await api.put("/onboarding/config", { sections, items });
      setCfg(r.data); setInitial(JSON.parse(JSON.stringify(r.data)));
      flash("ok", "Saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const reset = () => setCfg(JSON.parse(JSON.stringify(initial)));

  const addSection = () => {
    const id = `sec_${Date.now().toString(36)}`;
    setCfg({ ...cfg, sections: [...cfg.sections, { id, title: "New section", description: "", order: cfg.sections.length }] });
  };
  const removeSection = async (id) => {
    if (cfg.sections.length <= 1) { flash("error", "At least one section is required."); return; }
    if (cfg.items.some(it => it.section_id === id)) {
      const ok = await confirm({
        title: "Delete section with items?",
        description: "This section still has items. Deleting it will also remove all of its items.",
        confirmText: "Delete section",
        variant: "destructive",
      });
      if (!ok) return;
    }
    setCfg({
      ...cfg,
      sections: cfg.sections.filter(s => s.id !== id),
      items: cfg.items.filter(it => it.section_id !== id),
    });
  };
  const moveSection = (idx, dir) => {
    const arr = [...cfg.sections];
    const ni = idx + dir; if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setCfg({ ...cfg, sections: arr });
  };

  const updateSection = (idx, patch) => {
    const arr = [...cfg.sections]; arr[idx] = { ...arr[idx], ...patch };
    setCfg({ ...cfg, sections: arr });
  };

  const addItem = (sectionId) => {
    const key = `field_${Date.now().toString(36)}`;
    setCfg({
      ...cfg,
      items: [...cfg.items, {
        id: `oi_${key}`, section_id: sectionId, key, label: "New field",
        description: "", type: "text", required: false, order: cfg.items.length,
      }],
    });
  };

  const updateItem = (idx, patch) => {
    const arr = [...cfg.items]; arr[idx] = { ...arr[idx], ...patch };
    setCfg({ ...cfg, items: arr });
  };

  const removeItem = (idx) => {
    setCfg({ ...cfg, items: cfg.items.filter((_, i) => i !== idx) });
  };

  const moveItem = (idx, dir) => {
    const arr = [...cfg.items];
    const ni = idx + dir; if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setCfg({ ...cfg, items: arr });
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const sectionsSorted = cfg.sections;

  return (
    <div>
      <div className="eyebrow flex items-center gap-2 mb-1"><Rocket size={12} weight="bold" /> Checklist builder</div>
      <h2 className="font-display text-2xl font-black tracking-tight mb-1">Onboarding form</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Define the sections and fields new hires will fill in during onboarding. Reorder with the arrows,
        mark items as required, and change field types.
      </p>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border flex items-center gap-2 ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
          {msg.text}
        </div>
      )}

      <div className="space-y-6">
        {sectionsSorted.map((s, sIdx) => {
          const items = cfg.items.map((it, i) => ({ ...it, _idx: i })).filter(it => it.section_id === s.id);
          return (
            <section key={s.id} className="border border-border p-4" data-testid={`section-${s.id}`}>
              <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                <div className="flex-1 min-w-[250px] space-y-2">
                  <input
                    value={s.title}
                    onChange={e => updateSection(sIdx, { title: e.target.value })}
                    className="w-full border border-border px-3 py-2 text-lg font-display font-black"
                    data-testid={`section-title-${s.id}`}
                  />
                  <input
                    value={s.description || ""}
                    onChange={e => updateSection(sIdx, { description: e.target.value })}
                    placeholder="Description (optional)"
                    className="w-full border border-border px-3 py-2 text-xs text-muted-foreground"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} className="border border-border p-1.5 hover:bg-muted disabled:opacity-30"><ArrowUp size={12} weight="bold" /></button>
                  <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sectionsSorted.length - 1} className="border border-border p-1.5 hover:bg-muted disabled:opacity-30"><ArrowDown size={12} weight="bold" /></button>
                  <button onClick={() => removeSection(s.id)} className="border border-[#FF3B30] text-[#FF3B30] p-1.5 hover:bg-red-50" data-testid={`del-section-${s.id}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {items.map(it => (
                  <div key={it.id || it.key} className="border border-border p-3" data-testid={`item-${it.key}`}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                      <div className="md:col-span-4">
                        <label className="eyebrow block mb-1">Label</label>
                        <input
                          value={it.label}
                          onChange={e => updateItem(it._idx, { label: e.target.value })}
                          className="w-full border border-border px-2 py-1.5 text-sm"
                          data-testid={`item-label-${it.key}`}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="eyebrow block mb-1">Key</label>
                        <input
                          value={it.key}
                          onChange={e => updateItem(it._idx, { key: slug(e.target.value) })}
                          className="w-full border border-border px-2 py-1.5 text-sm font-mono-mini"
                          data-testid={`item-key-${it.key}`}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="eyebrow block mb-1">Type</label>
                        <select
                          value={it.type}
                          onChange={e => updateItem(it._idx, { type: e.target.value })}
                          className="w-full border border-border px-2 py-1.5 text-sm"
                          data-testid={`item-type-${it.key}`}
                        >
                          {ITEM_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2 flex items-end justify-between">
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!it.required}
                            onChange={e => updateItem(it._idx, { required: e.target.checked })}
                            className="accent-[#002FA7]"
                            data-testid={`item-required-${it.key}`}
                          />
                          Required
                        </label>
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveItem(it._idx, -1)} className="border border-border p-1 hover:bg-muted"><ArrowUp size={10} weight="bold" /></button>
                          <button onClick={() => moveItem(it._idx, 1)} className="border border-border p-1 hover:bg-muted"><ArrowDown size={10} weight="bold" /></button>
                          <button onClick={() => removeItem(it._idx)} className="border border-[#FF3B30] text-[#FF3B30] p-1 hover:bg-red-50"><Trash size={10} weight="bold" /></button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <label className="eyebrow block mb-1">Help text (optional)</label>
                      <input
                        value={it.description || ""}
                        onChange={e => updateItem(it._idx, { description: e.target.value })}
                        placeholder="Shown below the field"
                        className="w-full border border-border px-2 py-1.5 text-xs"
                      />
                    </div>

                    {it.type === "select" && (
                      <div className="mt-2">
                        <label className="eyebrow block mb-1">Options (one per line, format: value|label)</label>
                        <textarea
                          value={(it.options || []).map(o => `${o.value}|${o.label}`).join("\n")}
                          onChange={e => {
                            const opts = e.target.value.split("\n").map(l => {
                              const [v, ...rest] = l.split("|");
                              return { value: (v || "").trim(), label: (rest.join("|") || v || "").trim() };
                            }).filter(o => o.value);
                            updateItem(it._idx, { options: opts });
                          }}
                          rows={3}
                          className="w-full border border-border px-2 py-1.5 text-xs font-mono"
                          placeholder="active|Active\ninactive|Inactive"
                        />
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={() => addItem(s.id)} className="border border-dashed border-border px-3 py-2 text-sm hover:bg-muted w-full flex items-center justify-center gap-1" data-testid={`add-item-${s.id}`}>
                  <Plus size={12} weight="bold" /> Add field to this section
                </button>
              </div>
            </section>
          );
        })}

        <button onClick={addSection} className="border border-dashed border-border px-3 py-3 text-sm hover:bg-muted w-full flex items-center justify-center gap-1" data-testid="add-section">
          <Plus size={14} weight="bold" /> Add new section
        </button>
      </div>

      <div className="sticky bottom-0 bg-white border-t border-border pt-4 mt-6 flex items-center gap-2 justify-end">
        <button onClick={reset} disabled={!dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40 flex items-center gap-2">
          <ArrowClockwise size={14} weight="bold" /> Reset
        </button>
        <button onClick={save} disabled={!dirty || saving} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="save-onboarding-config">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
